<template>
  <div class="dash-wrapper">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <i class="dx-icon dx-icon-chart header-icon" />
        <div>
          <div class="header-title">Dashboard Stocuri</div>
          <div class="header-subtitle">Situație generală · Evoluție mișcări · Top produse</div>
        </div>
        <div class="header-right">
          <DxSelectBox
            v-model:value="filterGestiuneId"
            :data-source="gestiuni"
            display-expr="Nume"
            value-expr="Id"
            placeholder="Toate gestiunile"
            :show-clear-button="true"
            :width="220"
            styling-mode="outlined"
            class="gestiune-select"
          />
          <DxButton icon="refresh" :hint="'Actualizează'" @click="loadDate"
            :disabled="isLoading" styling-mode="text" class="refresh-btn" />
        </div>
      </div>
    </div>

    <div class="dash-body">
      <DxLoadPanel
        :visible="isLoading"
        :show-indicator="true"
        :show-pane="true"
        message="Se încarcă datele..."
        :position="{ of: '.dash-body' }"
      />

      <!-- KPI Cards -->
      <div class="kpi-row">
        <div class="kpi-card">
          <div class="kpi-icon" style="background: linear-gradient(135deg,#2563eb,#60a5fa)">
            <i class="dx-icon dx-icon-product" />
          </div>
          <div class="kpi-body">
            <div class="kpi-val">{{ totalProduse.toLocaleString('ro-RO') }}</div>
            <div class="kpi-lbl">Produse în stoc</div>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon" style="background: linear-gradient(135deg,#059669,#34d399)">
            <i class="dx-icon dx-icon-money" />
          </div>
          <div class="kpi-body">
            <div class="kpi-val">{{ fmtMoney(valoareTotala) }}</div>
            <div class="kpi-lbl">Valoare totală stoc</div>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon" style="background: linear-gradient(135deg,#7c3aed,#a78bfa)">
            <i class="dx-icon dx-icon-group" />
          </div>
          <div class="kpi-body">
            <div class="kpi-val">{{ gestiuniActive }}</div>
            <div class="kpi-lbl">Gestiuni active</div>
          </div>
        </div>
        <div class="kpi-card" :class="stocNegativCount > 0 ? 'kpi-alert' : ''">
          <div class="kpi-icon" :style="stocNegativCount > 0
            ? 'background: linear-gradient(135deg,#dc2626,#f87171)'
            : 'background: linear-gradient(135deg,#64748b,#94a3b8)'">
            <i class="dx-icon dx-icon-warning" />
          </div>
          <div class="kpi-body">
            <div class="kpi-val" :class="stocNegativCount > 0 ? 'val-red' : ''">
              {{ stocNegativCount }}
            </div>
            <div class="kpi-lbl">Produse stoc negativ</div>
          </div>
        </div>
        <div class="kpi-card" :class="faraMiscareCount > 0 ? 'kpi-warn' : ''">
          <div class="kpi-icon" :style="faraMiscareCount > 0
            ? 'background: linear-gradient(135deg,#d97706,#fbbf24)'
            : 'background: linear-gradient(135deg,#64748b,#94a3b8)'">
            <i class="dx-icon dx-icon-clock" />
          </div>
          <div class="kpi-body">
            <div class="kpi-val" :class="faraMiscareCount > 0 ? 'val-orange' : ''">
              {{ faraMiscareCount }}
            </div>
            <div class="kpi-lbl">Fără mișcare ({{ anCurent }})</div>
          </div>
        </div>
      </div>

      <!-- Chart + Top produse -->
      <div class="content-row">
        <!-- Grafic mișcări lunare -->
        <div class="chart-card">
          <div class="section-header">
            <i class="dx-icon dx-icon-chart" />
            <span>Evoluție mișcări {{ anCurent }}</span>
            <div class="chart-legend">
              <span class="leg-dot" style="background:#059669" /> Intrări
              <span class="leg-dot" style="background:#dc2626; margin-left:12px" /> Ieșiri
            </div>
          </div>
          <div class="chart-body">
            <DxChart
              v-if="chartData.length"
              :data-source="chartData"
              :animation="{ enabled: true }"
              :palette="['#059669','#dc2626']"
            >
              <DxCommonSeriesSettings argument-field="luna" type="bar" :bar-padding="0.2" />
              <DxSeries value-field="ValoareIntrata" name="Intrări" color="#059669" />
              <DxSeries value-field="ValoareIesita" name="Ieșiri" color="#dc2626" />
              <DxArgumentAxis>
                <DxLabel :word-wrap="'none'" :overlapping-behavior="'none'" />
              </DxArgumentAxis>
              <DxValueAxis>
                <DxLabel :customize-text="formatAxisVal" />
              </DxValueAxis>
              <DxLegend
                vertical-alignment="bottom"
                horizontal-alignment="center"
                :visible="false"
              />
              <DxTooltip
                :enabled="true"
                :customize-tooltip="customizeTooltip"
              />
            </DxChart>
            <div v-else-if="!isLoading" class="empty-chart">
              <i class="dx-icon dx-icon-chart" style="font-size:40px;color:#e2e8f0" />
              <div style="color:#94a3b8;font-size:13px;margin-top:8px">Nu există mișcări în {{ anCurent }}</div>
            </div>
          </div>
        </div>

        <!-- Top produse după valoare -->
        <div class="top-card">
          <div class="section-header">
            <i class="dx-icon dx-icon-orderedlist" />
            <span>Top 20 produse după valoare stoc</span>
          </div>
          <DxDataGrid
            :data-source="topProduse"
            height="100%"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="true"
            :row-alternation-enabled="true"
            :hover-state-enabled="true"
            :column-auto-width="false"
          >
            <DxPaging :page-size="20" />
            <DxSorting mode="single" />
            <DxColumn :width="30" caption="#" cell-template="rankTemplate" />
            <DxColumn data-field="ProdusNume" caption="Produs" :min-width="130" css-class="col-produs" />
            <DxColumn data-field="Stoc" caption="Stoc" :width="80" data-type="number"
              :format="{ type: 'fixedPoint', precision: 2 }" />
            <DxColumn data-field="Valoare" caption="Valoare" :width="110" data-type="number"
              :format="{ type: 'fixedPoint', precision: 2 }" cell-template="valTemplate"
              :sort-index="0" sort-order="desc" />
            <template #rankTemplate="{ rowIndex }">
              <span class="rank-badge" :class="rankClass(rowIndex)">{{ rowIndex + 1 }}</span>
            </template>
            <template #valTemplate="{ data }">
              <span class="val-bold">{{ fmtNum(data.value, 2) }}</span>
            </template>
          </DxDataGrid>
        </div>
      </div>

      <!-- Bara rezumat mișcări -->
      <div v-if="totalIntrat > 0 || totalIesit > 0" class="summary-bar">
        <div class="sum-item">
          <span class="sum-lbl">Total intrări {{ anCurent }}</span>
          <span class="sum-val green">{{ fmtMoney(totalIntrat) }}</span>
        </div>
        <div class="sum-divider" />
        <div class="sum-item">
          <span class="sum-lbl">Total ieșiri {{ anCurent }}</span>
          <span class="sum-val red">{{ fmtMoney(totalIesit) }}</span>
        </div>
        <div class="sum-divider" />
        <div class="sum-item">
          <span class="sum-lbl">Sold net</span>
          <span class="sum-val" :class="totalIntrat - totalIesit >= 0 ? 'green' : 'red'">
            {{ fmtMoney(totalIntrat - totalIesit) }}
          </span>
        </div>
        <div class="sum-divider" />
        <div class="sum-item">
          <span class="sum-lbl">Nr. mișcări</span>
          <span class="sum-val">{{ totalMiscari.toLocaleString('ro-RO') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import {
  DxChart, DxSeries, DxCommonSeriesSettings,
  DxArgumentAxis, DxValueAxis, DxLabel,
  DxLegend, DxTooltip,
} from "devextreme-vue/chart";
import {
  DxDataGrid, DxColumn, DxPaging, DxSorting,
} from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxButton } from "devextreme-vue/button";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import http from "@/settings/apiSettings";
import GES_StocService from "@/services/GES/GES_Stoc";

const MONTHS_RO = ['Ian','Feb','Mar','Apr','Mai','Iun','Iul','Aug','Sep','Oct','Nov','Dec'];

const gestiuni = ref<any[]>([]);
const isLoading = ref(false);
const filterGestiuneId = ref<string | null>(null);

const anCurent = new Date().getFullYear();
const yearStart = `${anCurent}-01-01`;
const today = new Date().toISOString().split("T")[0];

// Stoc snapshot
const stocData = ref<any[]>([]);
// Mișcări an curent
const miscariData = ref<any[]>([]);

onMounted(async () => {
  try {
    const resp = await http.clientStocuriOdata.get("GES_Gestiuni");
    gestiuni.value = resp.data?.value ?? resp.data ?? [];
  } catch { /* silent */ }
  await loadDate();
});

async function loadDate() {
  isLoading.value = true;
  const gestiuneId = filterGestiuneId.value ?? undefined;
  try {
    const [stocResp, miscariResp] = await Promise.all([
      GES_StocService.getStocCurent(today, gestiuneId, 0, true),
      GES_StocService.getMiscari(yearStart, today, gestiuneId),
    ]);
    stocData.value = stocResp.data ?? [];
    miscariData.value = miscariResp.data ?? [];
  } catch { /* silent */ } finally {
    isLoading.value = false;
  }
}

// KPI
const totalProduse = computed(() => stocData.value.filter(s => (s.Stoc ?? 0) > 0).length);
const valoareTotala = computed(() => stocData.value.reduce((acc, s) => acc + (s.Valoare ?? 0), 0));
const gestiuniActive = computed(() => new Set(stocData.value.filter(s => (s.Stoc ?? 0) > 0).map(s => s.GestiuneId)).size);
const stocNegativCount = computed(() => stocData.value.filter(s => (s.Stoc ?? 0) < 0).length);

const faraMiscareCount = computed(() => {
  const withMovement = new Set(miscariData.value.map(m => `${m.ProdusId}|${m.GestiuneId}`));
  return stocData.value.filter(s => (s.Stoc ?? 0) > 0 && !withMovement.has(`${s.ProdusId}|${s.GestiuneId}`)).length;
});

// Top 20 produse după valoare
const topProduse = computed(() =>
  [...stocData.value]
    .filter(s => (s.Stoc ?? 0) > 0)
    .sort((a, b) => (b.Valoare ?? 0) - (a.Valoare ?? 0))
    .slice(0, 20)
);

// Chart data — mișcări groupate pe lună
interface MonthStat {
  luna: string;
  lunaIdx: number;
  ValoareIntrata: number;
  ValoareIesita: number;
  NrIntrari: number;
  NrIesiri: number;
}

const chartData = computed<MonthStat[]>(() => {
  const map = new Map<number, MonthStat>();
  for (let i = 0; i < 12; i++) {
    const d = new Date(anCurent, i, 1);
    if (d > new Date()) break;
    map.set(i, { luna: MONTHS_RO[i], lunaIdx: i, ValoareIntrata: 0, ValoareIesita: 0, NrIntrari: 0, NrIesiri: 0 });
  }
  for (const m of miscariData.value) {
    const luna = new Date(m.Data).getMonth();
    const entry = map.get(luna);
    if (!entry) continue;
    if (m.TipMiscare === "Intrare") {
      entry.ValoareIntrata += m.Valoare ?? 0;
      entry.NrIntrari++;
    } else {
      entry.ValoareIesita += m.Valoare ?? 0;
      entry.NrIesiri++;
    }
  }
  return Array.from(map.values());
});

const totalIntrat = computed(() => miscariData.value.filter(m => m.TipMiscare === "Intrare").reduce((a, m) => a + (m.Valoare ?? 0), 0));
const totalIesit = computed(() => miscariData.value.filter(m => m.TipMiscare === "Ieșire").reduce((a, m) => a + (m.Valoare ?? 0), 0));
const totalMiscari = computed(() => miscariData.value.length);

// Helpers
function fmtMoney(val: number): string {
  if (val >= 1_000_000) return (val / 1_000_000).toFixed(2) + " M";
  if (val >= 1_000) return (val / 1_000).toFixed(1) + " K";
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
}

function fmtNum(val: number, dec: number): string {
  return new Intl.NumberFormat("ro-RO", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(val ?? 0);
}

function formatAxisVal(e: any): string {
  const v = e.value as number;
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(0) + "K";
  return v.toFixed(0);
}

function customizeTooltip(point: any): object {
  return {
    text: `${point.seriesName}\n${fmtNum(point.value, 2)} lei`,
  };
}

function rankClass(idx: number): string {
  if (idx === 0) return "rank-gold";
  if (idx === 1) return "rank-silver";
  if (idx === 2) return "rank-bronze";
  return "rank-default";
}
</script>

<style scoped>
.dash-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f8f9fc;
  overflow: hidden;
}

/* Header */
.page-header {
  background: linear-gradient(90deg, #ff2c21, #ff6a4a);
  padding: 12px 20px;
  flex-shrink: 0;
}
.header-content { display: flex; align-items: center; gap: 14px; }
.header-icon { font-size: 28px; color: white; }
.header-title { font-size: 16px; font-weight: 700; color: white; }
.header-subtitle { font-size: 12px; color: rgba(255,255,255,0.85); margin-top: 2px; }
.header-right { margin-left: auto; display: flex; align-items: center; gap: 8px; }

:deep(.gestiune-select .dx-texteditor-container) {
  background: rgba(255,255,255,0.15);
  border-color: rgba(255,255,255,0.3);
}
:deep(.gestiune-select .dx-texteditor-input),
:deep(.gestiune-select .dx-placeholder) {
  color: white !important;
}
:deep(.refresh-btn) { color: white !important; }

/* Body */
.dash-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  overflow-y: auto;
  position: relative;
}

/* KPI row */
.kpi-row {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
.kpi-card {
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: box-shadow 0.15s;
}
.kpi-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
.kpi-card.kpi-alert { border-left: 3px solid #dc2626; }
.kpi-card.kpi-warn  { border-left: 3px solid #d97706; }
.kpi-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.kpi-icon i { font-size: 20px; color: white; }
.kpi-val { font-size: 22px; font-weight: 700; color: #1e293b; line-height: 1.2; }
.kpi-lbl { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px; }
.val-red    { color: #dc2626; }
.val-orange { color: #d97706; }

/* Content row */
.content-row {
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
}

/* Chart card */
.chart-card {
  flex: 3;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 280px;
}

/* Top card */
.top-card {
  flex: 2;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 280px;
}

/* Section header */
.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  flex-shrink: 0;
}
.section-header i { color: #94a3b8; }

.chart-legend {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #64748b;
  font-weight: 400;
}
.leg-dot {
  display: inline-block;
  width: 10px; height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}

.chart-body {
  flex: 1;
  min-height: 0;
  padding: 8px 12px 12px;
  display: flex;
  flex-direction: column;
}
.chart-body :deep(.dx-chart) { flex: 1; height: 100%; }

.empty-chart {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Top grid */
:deep(.col-produs) { font-size: 12px; }

.rank-badge {
  width: 22px; height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}
.rank-gold   { background: #fef3c7; color: #92400e; }
.rank-silver { background: #f1f5f9; color: #475569; }
.rank-bronze { background: #fff7ed; color: #9a3412; }
.rank-default { background: #f8fafc; color: #94a3b8; }

.val-bold { font-weight: 600; color: #1e293b; }

/* Summary bar */
.summary-bar {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 0;
  flex-shrink: 0;
}
.sum-item { display: flex; flex-direction: column; align-items: center; flex: 1; gap: 2px; }
.sum-lbl { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.sum-val { font-size: 18px; font-weight: 700; color: #1e293b; }
.sum-val.green { color: #059669; }
.sum-val.red   { color: #dc2626; }
.sum-divider { width: 1px; height: 36px; background: #f1f5f9; flex-shrink: 0; }
</style>
